<template>
  <MainLayout>
    <ContentPageHeader functionName="価格操作管理" pageName="値上げ率一覧" />
    
    <!-- 最終更新日表示 -->
    <div class="d-flex justify-content-end mb-3" v-if="latestUpdateDate">
      <span class="text-muted latest-update-date">最終更新日：{{ latestUpdateDate }}</span>
    </div>
    
    <div class="card mb-4">
      <div class="card-body">
        <PriceIncreaseTable
          :items="tableItems"
          :columns="columns"
          @edit="handleEdit"
        />
      </div>
    </div>
    
    <!-- CommonModalを使用した編集モーダル -->
    <CommonModal
      :visible="showEditModal"
      title="値上げ率編集"
      @close="closeEditModal"
      @ok="saveChanges"
      width="90%"
    >
      <div v-if="editingData">
        <div class="mb-3">
          <label class="form-label">カテゴリ</label>
          <input type="text" class="form-control" :value="editingData.category" disabled />
        </div>
        <div class="mb-3">
          <label class="form-label">販売チャンネル</label>
          <input type="text" class="form-control" :value="getSalesChannelName(editingData.channel.salesChannel)" disabled />
        </div>
        
        <!-- 値上げ率詳細編集 -->
        <div class="mb-3">
          <label class="form-label">値上げ率設定</label>
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 35%">販売価格 (円)</th>
                  <th style="width: 20%">値上げ率 (%)</th>
                  <th style="width: 20%">送料 (¥)</th>
                  <th style="width: 15%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, index) in editingDetails" :key="`edit-${index}`">
                  <td>
                    <div class="d-flex align-items-center">
                      <input type="number" class="form-control" v-model="detail.sellingPriceMin" disabled />
                      <span class="mx-2">~</span>
                      <input type="number" class="form-control" v-model="detail.sellingPriceMax" 
                             :id="'priceMax-' + detail.index" 
                             :disabled="index === editingDetails.length - 1"
                             @input="sortedPriceIncrease(detail.index)" />
                    </div>
                  </td>
                  <td>
                    <input type="number" class="form-control" v-model="detail.priceIncreaseRate" />
                  </td>
                  <td>
                    <input type="number" class="form-control" v-model="detail.shippingFee" />
                  </td>
                  <td>
                    <button v-if="index !== editingDetails.length - 1" class="btn btn-danger" @click="removeDetail(index)">削除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button class="btn btn-secondary mt-2" @click="addDetail">行追加</button>
        </div>
      </div>
    </CommonModal>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import CommonModal from '@/components/common/CommonModal.vue'
import PriceIncreaseTable from './PriceIncreaseTable.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'PriceIncreaseListView',
  components: {
    MainLayout,
    ContentPageHeader,
    CommonModal,
    PriceIncreaseTable
  },
  data() {
    return {
      priceIncreaseList: {},
      salesChannels: [],
      childCategories: [],
      editingData: null,
      editingDetails: [],
      showEditModal: false,
      columns: [
        { key: 'category', label: 'カテゴリ', minWidth: 150 },
        { key: 'salesChannel', label: '販売チャンネル', minWidth: 150 },
        { key: 'priceRange', label: '販売価格 (円)', minWidth: 150 },
        { key: 'increaseRate', label: '値上げ率 (%)', minWidth: 100 },
        { key: 'shippingFee', label: '送料 (¥)', minWidth: 100 },
        { key: 'actions', label: '操作', minWidth: 80 }
      ],
      actions: [],
      latestUpdateDate: '', // APIから取得する値を格納
    }
  },
  computed: {
    tableItems() {
      const items = [];
      
      // priceIncreaseListをテーブル表示用に変換
      Object.entries(this.priceIncreaseList).forEach(([category, channelList]) => {
        // 各カテゴリの最初の行のみカテゴリ名を表示
        let isFirstChannelInCategory = true;
        
        channelList.forEach((channel) => {
          // 価格範囲、値上げ率、送料の配列を作成（最小価格でソート）
          const sortedDetails = [...channel.details].sort((a, b) => a.sellingPriceMin - b.sellingPriceMin);
          
          const priceRanges = sortedDetails.map(detail => 
            this.formatPriceRange(detail.sellingPriceMin, detail.sellingPriceMax)
          );
          
          const increaseRates = sortedDetails.map(detail => detail.priceIncreaseRate);
          
          const shippingFees = sortedDetails.map(detail => detail.shippingFee);
          
          items.push({
            // 最初の行のみカテゴリ名を表示、それ以外は空文字
            category: isFirstChannelInCategory ? category : '',
            // カテゴリのグループ化のために元のカテゴリ名を保持
            originalCategory: category,
            salesChannel: this.getSalesChannelName(channel.salesChannel),
            priceRange: priceRanges,
            increaseRate: increaseRates,
            shippingFee: shippingFees,
            originalChannel: channel // 編集用に元のデータを保持
          });
          
          // 次の行からはカテゴリ名を表示しない
          isFirstChannelInCategory = false;
        });
      });
      
      return items;
    }
  },
  async mounted() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        showLoading();
        
        // 複数リクエストを並列で実行
        const [channelsResponse, categoriesResponse, priceIncreaseResponse, latestUpdateResponse] = await Promise.all([
          service.getSalesChannelList(),
          service.getCategoryList(),
          service.getPriceIncreaseList(),
          service.getPriceOperationLatestUpdateDate(true)
        ]);

        // 販売チャンネル一覧チェック
        if (!channelsResponse || !channelsResponse.data) {
          throw new Error('販売チャンネル一覧の取得に失敗しました');
        }
        this.salesChannels = channelsResponse.data.salesChannels;

        // カテゴリ一覧チェック
        if (!categoriesResponse || !categoriesResponse.data) {
          throw new Error('カテゴリ一覧の取得に失敗しました');
        }
        this.childCategories = categoriesResponse.data.categories.flatMap(category => category.childCategories);

        // 値上げ情報一覧チェック
        if (!priceIncreaseResponse || !priceIncreaseResponse.data) {
          throw new Error('値上げ情報一覧の取得に失敗しました');
        }
        this.priceIncreaseList = priceIncreaseResponse.data;

        // 最終更新日時をセット
        this.latestUpdateDate = latestUpdateResponse && latestUpdateResponse.data ? latestUpdateResponse.data : null;

      } catch (error) {
        console.error('データ取得エラー:', error);
        alert('データ取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    getSalesChannelName(id) {
      const channel = this.salesChannels.find(ch => ch.id === id)
      return channel ? channel.acc : ''
    },
    formatPriceRange(min, max) {
      // データが1行かつ下限も上限もnullの場合は"指定なし"
      if ((min === null || min === 0) && (max === null || max === 0)) {
        return '指定なし';
      }
      // 下限がnullで上限がある場合は下限を空白として表示
      if (min === null || min === 0) {
        return `~ ${max}`;
      }
      // 上限がnullの場合
      if (max === null || max === 0) {
        return `${min} ~`;
      }
      // 両方ある場合
      return `${min} ~ ${max}`;
    },
    getCategoryIdByName(name) {
      const category = this.childCategories.find(cat => cat.category === name)
      return category ? category.id : null
    },
    handleEdit(data) {
      const categoryName = data.category;
      const channel = data.channel;
      
      this.editingData = {
        category: categoryName,
        channel,
        categoryId: this.getCategoryIdByName(categoryName)
      }
      
      // 編集用データの作成（最小価格でソート）
      this.editingDetails = 0 < channel.details.length 
        ? JSON.parse(JSON.stringify(channel.details))
            .sort((a, b) => a.sellingPriceMin - b.sellingPriceMin)
            .map((item, index) => ({ ...item, index }))
        : [{ index: 0, sellingPriceMin: 1, sellingPriceMax: null, priceIncreaseRate: 0, shippingFee: 0 }]
      // モーダルを表示
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.editingData = null
      this.editingDetails = []
    },
    addDetail() {
      // 新しい行を追加
      const newIndex = Math.max(...this.editingDetails.map(item => item.index));
      const newItem = {
        index: newIndex + 1,
        id: null,
        sellingPriceMin: null,
        sellingPriceMax: null,
        priceIncreaseRate: 0,
        shippingFee: 0
      };
      const insertIndex = Math.max(this.editingDetails.length - 1, 0);
      this.editingDetails.splice(insertIndex, 0, newItem);


      this.$nextTick(() => {
        const currentId = `priceMax-${newIndex + 1}`;
        document.getElementById(currentId).focus();
      });
    },

    /**
     * 値上げ詳細のバリデーションチェックを行う
     * @returns {Object} バリデーション結果 { isValid: boolean, message: string }
     */
    validatePriceIncreaseDetails() {
      // 値上げ率と送料が入力されているかチェック
      for (let i = 0; i < this.editingDetails.length; i++) {
        const detail = this.editingDetails[i]
        
        // 値上げ率が未入力の場合
        if (detail.priceIncreaseRate === null || detail.priceIncreaseRate === undefined || detail.priceIncreaseRate === '') {
          return {
            isValid: false,
            message: `${i+1}行目の値上げ率を入力してください。`
          }
        }
        
        // 送料が未入力の場合
        if (detail.shippingFee === null || detail.shippingFee === undefined || detail.shippingFee === '') {
          return {
            isValid: false,
            message: `${i+1}行目の送料を入力してください。`
          }
        }
      }
      const maxPriceSet = new Set();
      for (let i = 0; i < this.editingDetails.length; i++) {
        const maxPrice = this.editingDetails[i].sellingPriceMax;

        // 最後の要素以外で、上限金額が未入力ならエラー
        if (i !== this.editingDetails.length - 1 && (maxPrice === null || maxPrice === undefined || maxPrice === '')) {
          return {
            isValid: false,
            message: `${i + 1}行目の上限金額を入力してください。`
          };
        }

        // 上限金額の重複エラー
        if (maxPrice !== null && maxPrice !== undefined && maxPrice !== '' && maxPriceSet.has(maxPrice)) {
          return {
            isValid: false,
            message: `上限金額が重複しています。上限金額を正しく設定してください。`
          };
        }
        
        if (maxPrice !== null && maxPrice !== undefined && maxPrice !== '') {
          maxPriceSet.add(maxPrice);
        }
      }
      
      // すべてのチェックをパスした場合
      return { isValid: true, message: '' }
    },
    
    // 自動ソートと下限金額の自動設定を行うメソッド
    sortedPriceIncrease(index) {
      // 上限金額がnullのデータが2件以上あるか確認
      const nullMaxCount = this.editingDetails.filter(detail => 
        detail.sellingPriceMax === null || detail.sellingPriceMax === undefined || detail.sellingPriceMax === ''
      ).length;
      if (nullMaxCount >= 2) return;
      
      // 1. ソート
      this.editingDetails.sort((a, b) => {
        const aMax = a.sellingPriceMax ?? Infinity;
        const bMax = b.sellingPriceMax ?? Infinity;
        return aMax - bMax;
      });

      // 2. 下限金額を設定
      for (let i = 0; i < this.editingDetails.length; i++) {
        if (i === 0) {
          // 先頭だけ常にnullにする
          this.editingDetails[i].sellingPriceMin = null;
          continue;
        }
        
        const prevMax = this.editingDetails[i - 1].sellingPriceMax;
        this.editingDetails[i].sellingPriceMin = (prevMax != null) ? prevMax + 1 : null;
      }

      // 3.フォーカスされていた要素が存在する場合、その要素に再フォーカス
      this.$nextTick(() => {
        if (index === undefined) {
          return;
        }
        const currentId = `priceMax-${index}`;
        document.getElementById(currentId).focus();
      });
    },
    removeDetail(index) {
      this.editingDetails.splice(index, 1);

      // 配列が1件だけになった場合、その要素のsellingPriceMinをnullにする
      if (this.editingDetails.length === 1) {
        this.editingDetails[0].sellingPriceMin = null;
      }
      
      this.sortedPriceIncrease();
    },
    
    async saveChanges() {
      if (!this.editingData || !this.editingData.categoryId) {
        alert('カテゴリ情報が取得できません。')
        return
      }
      
      // 保存前に自動ソートと下限値の自動設定を行う
      this.sortedPriceIncrease();
      
      // バリデーションチェック
      const validation = this.validatePriceIncreaseDetails()
      if (!validation.isValid) {
        alert(validation.message)
        return
      }
      
      // 保存確認ダイアログを表示
      if (!confirm('現在入力されている情報で更新してもよろしいですか？')) {
        return
      }
      
      try {
        showLoading();
        // 値上げ情報を保存
        const result = await service.updatePriceIncrease(
          this.editingData.categoryId,
          this.editingData.channel.salesChannel,
          this.editingDetails
        )
        
        if (!result.data.success) {
          alert('値上げ情報の更新に失敗しました。')
          return
        }
        
        // 成功したら一覧を再取得
        await this.fetchData()
        this.closeEditModal()
      } catch (error) {
        console.error('値上げ情報更新エラー:', error)
        alert('値上げ情報の更新中にエラーが発生しました。')
      } finally {
        hideLoading();
      }
    }
  }
}
</script>

<style scoped>
.card {
  margin-bottom: 20px;
}
.card-body {
  margin: 5px;
}

/* モーダルの幅を1.5倍に拡大 */
:deep(.common-modal-content) {
  max-width: 900px !important;
  width: 95% !important;
}

/* 入力フィールドの余白を調整 */
.table td {
  padding: 10px;
}

/* 最終更新日表示のスタイル */
.latest-update-date {
  font-weight: bold;
  padding: 10px 10px 0 0;
}
</style>
